// Pure, framework-free geometry/validation/payload helpers for the
// cross-line (越線) tripwire feature. Mirrors the backend pure module
// (`controller/indoor_positioning_tripwire.py`) and REST validation
// (`controller/indoor_positioning_tripwire_api.py`) formula-for-formula so
// the editor's A/B labels, arrows and client-side errors agree with what the
// server will actually do/accept. No React, no antd, no axios here -- keeps
// it trivially unit-testable and reusable from both the editor and any
// future consumer (e.g. a settings preview).

import { DEFAULT_LANE_SPAN_M } from "./constants";

// ---------------------------------------------------------------------------
// Side classification (spec "Coordinate frame")
// ---------------------------------------------------------------------------

/**
 * `s = (x2-x1)*(qy-y1) - (y2-y1)*(qx-x1)`.
 * Side A <=> s > 0, side B <=> s < 0. Identical formula to the backend's
 * `side_of_line` -- this is the one place a mismatch would silently make the
 * editor's A/B labels lie about which side is which.
 *
 * Worked example from the spec: p1=(0,0), p2=(10,0), q=(5,5) -> s=50 -> "A".
 * In the app's y-up metres frame (origin bottom-left) that means A is the
 * LEFT of travel when walking p1->p2, i.e. ABOVE a left-to-right line on
 * screen; B is the right of travel, below it. (Screen pixels are y-down, so
 * only the viewport projection ever sees the flipped sign.)
 */
export const sideOfLine = (p1, p2, q) => {
  return (p2[0] - p1[0]) * (q[1] - p1[1]) - (p2[1] - p1[1]) * (q[0] - p1[0]);
};

/** 'A' | 'B' | null (null = exactly on the infinite line). */
export const classifySide = (p1, p2, q) => {
  const s = sideOfLine(p1, p2, q);
  if (s > 0) return "A";
  if (s < 0) return "B";
  return null;
};

// ---------------------------------------------------------------------------
// Geometry: length, midpoint, normal, label anchors, arrows
// ---------------------------------------------------------------------------

export const segmentLength = (p1, p2) =>
  Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);

const midpoint = (p1, p2) => [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

/**
 * Unit normal pointing toward side A. `s`'s gradient w.r.t. q is
 * `(-(y2-y1), (x2-x1))`, which points in the direction of INCREASING s, i.e.
 * toward side A by definition (s > 0 <=> A). Degenerate (zero-length) lines
 * fall back to (0, 0) -- callers must not divide by it.
 */
export const normalTowardA = (p1, p2) => {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return [0, 0];
  return [-dy / len, dx / len];
};

// ---------------------------------------------------------------------------
// Direction (2026-09-22(c) revision): operator-facing direction collapses to
// two values -- 單向 (`a_to_b`, alarm entering the "restricted" side B) and
// 雙向 (`both`). `b_to_a` is a legacy/defensive value only: the backend
// normalises it away on every create/update (by swapping the two points and
// storing `a_to_b`), so live data should never carry it, but a not-yet-
// migrated row or an in-flight response could. `restrictedSides` handles it
// WITHOUT needing to touch the tripwire's actual point order/array indices
// (which the editor's drag handlers key by) -- it just shades the opposite
// side directly: for `b_to_a` (alarm entering A) the restricted side is A,
// the same physical region a real `a_to_b` line would get if its points were
// swapped (sideOfLine flips sign under point-order swap for the same pair of
// points), so this is provably equivalent without any array mutation. No A/B
// labels or direction arrows are drawn anywhere any more (this revision).
// ---------------------------------------------------------------------------

/** `true` for a "single-direction" line (`a_to_b`, or legacy `b_to_a`). */
export const isSingleDirection = (direction) => direction !== "both";

/**
 * Which side(s) of the line to shade as "restricted" (spec: side B = s<0
 * for `a_to_b`; both sides, lighter, for `both`). Legacy `b_to_a` shades A
 * -- see header comment for why that needs no point swap.
 */
export const restrictedSides = (direction) => {
  if (direction === "b_to_a") return ["A"];
  if (direction === "both") return ["A", "B"];
  return ["B"]; // a_to_b (and any other/unknown value defaults here too)
};

export const DIRECTION_OPTIONS = [
  { value: "a_to_b", label: "單向（進入淡色側告警）" },
  { value: "both", label: "雙向（任一方向越線皆告警）" },
];

const DIRECTION_HINTS = {
  a_to_b: "僅在跨越線段進入淡色側時告警；原本就在淡色側或離開淡色側不會告警。",
  both: "任一方向跨越線段皆告警。",
};

/** Field hint text for the direction Select -- changes with the selection. */
export const directionHint = (direction) =>
  DIRECTION_HINTS[direction] || DIRECTION_HINTS.a_to_b;

/**
 * Swaps a tripwire's two points -- used both by 「翻轉限制側」 (single-
 * direction lines only) and, identically, by a lane gate's flip (its
 * 2-point segment is just `[[x,0],[x,spanM]]`, so the same swap works).
 * Returns a PUT-ready partial payload (`{ points }`) for the existing
 * optimistic `useTripwires.update` path -- geometry (the physical segment)
 * is unchanged, only which end is "first" (and therefore which side is A)
 * flips.
 */
export const buildFlipPayload = (points) => {
  const [p1, p2] = points;
  return { points: [p2, p1] };
};

// ---------------------------------------------------------------------------
// Provisional (optimistic-create) tripwire ids
// ---------------------------------------------------------------------------

/**
 * `true` for a PROVISIONAL tripwire id -- one `useTripwires.create` assigned
 * locally (a decrementing negative integer, see its header comment) to a
 * line that has been inserted into state optimistically but whose POST has
 * not resolved yet, so the backend has never heard of it. Real server ids
 * are always positive integers, so a single sign check tells them apart.
 * Every consumer keyed by a tripwire's `id` (TripwireOverlay's drag
 * handles, TripwirePopover's 編輯/刪除/翻轉 buttons, FloorPlanModal's level-1
 * edit-open handler, and `useTripwires.update`/`remove` themselves) uses
 * this to refuse to fire a PUT/DELETE against a line that doesn't exist on
 * the server yet.
 */
export const isProvisionalTripwireId = (id) => typeof id === "number" && id < 0;

// ---------------------------------------------------------------------------
// Shaded restricted side (2026-09-22(c)): a short gradient extending
// perpendicular from the line. Computed in METRES (y-up frame) and projected
// by the viewport, so it composes with whatever zoom/pan/lane projection the
// caller already uses and the side is correct by construction.
// ---------------------------------------------------------------------------

export const DEFAULT_SHADING_DEPTH_PX = 32;
// Product owner (2026-09-23): "I don't like how the faded shades are
// different between one and both directions. Align the shades styles to the
// one that one-direction have now." -- ONE opacity for every shaded side,
// single-direction or both, so 雙向 reads at the same strength as 單向 on
// each side instead of a lighter "barrier" look. Everything else (32px
// depth, gradient fade to 0, line colour, the unfocused dimming factor
// applied in TripwireOverlay) is unchanged and applies equally regardless of
// direction.
export const SHADING_OPACITY = 0.35;

/**
 * Metres-space shading, one descriptor per restricted side:
 *   `{ side, opacityFrom, polygon: [p1, p2, p2+o, p1+o], gradient: {from, to} }`
 * where `o = n * depthMetres * (A ? +1 : -1)` and `n` is `normalTowardA`
 * (travel rotated 90 degrees CCW, i.e. the left of travel in y-up). The
 * gradient runs from the line's midpoint to the midpoint offset by `o`.
 * Degenerate (zero-length) lines return `[]` (nothing to shade).
 */
export const restrictedSidePolygons = (p1m, p2m, direction, depthMetres) => {
  if (segmentLength(p1m, p2m) < 1e-9) return [];
  const [nx, ny] = normalTowardA(p1m, p2m);
  const mid = midpoint(p1m, p2m);

  return restrictedSides(direction).map((side) => {
    const sign = side === "A" ? 1 : -1;
    const ox = nx * depthMetres * sign;
    const oy = ny * depthMetres * sign;
    return {
      side,
      opacityFrom: SHADING_OPACITY,
      polygon: [
        p1m,
        p2m,
        [p2m[0] + ox, p2m[1] + oy],
        [p1m[0] + ox, p1m[1] + oy],
      ],
      gradient: { from: mid, to: [mid[0] + ox, mid[1] + oy] },
    };
  });
};

// Same output shape the SVG layers consume:
// `{ side, opacityFrom, polygon: [[x,y]x4], gradient: {x1,y1,x2,y2} }`.
const toPixelShading = (shading, mapPoint) => {
  const from = mapPoint(shading.gradient.from);
  const to = mapPoint(shading.gradient.to);
  return {
    side: shading.side,
    opacityFrom: shading.opacityFrom,
    polygon: shading.polygon.map(mapPoint),
    gradient: { x1: from[0], y1: from[1], x2: to[0], y2: to[1] },
  };
};

/**
 * Shading for a tripwire given in metres, projected through `viewport`.
 * `depthPx` stays a fixed SCREEN depth under zoom: it is converted to metres
 * with `viewport.pxPerMeter` (which already includes zoom), so a lane gate's
 * strip is `depthPx` wide at any zoom. An invalid viewport returns `[]`.
 */
export const projectShading = (
  viewport,
  p1m,
  p2m,
  direction,
  depthPx = DEFAULT_SHADING_DEPTH_PX,
) => {
  if (!viewport?.isValid || !(viewport.pxPerMeter > 0)) return [];
  const mapPoint = (p) => viewport.projectPoints([p])[0];
  return restrictedSidePolygons(
    p1m,
    p2m,
    direction,
    depthPx / viewport.pxPerMeter,
  ).map((s) => toPixelShading(s, mapPoint));
};

// ---------------------------------------------------------------------------
// Level-1 popover placement: keep the popover clear of both endpoint
// handles (spec addendum -- a midpoint-anchored popover can cover a handle
// on a short/slanted line, making it untouchable).
// ---------------------------------------------------------------------------

export const TRIPWIRE_HANDLE_RADIUS_PX = 5;
export const POPOVER_CLEARANCE_GAP_PX = 12;

/**
 * Anchor point + antd `placement` for the level-1 popover, computed from the
 * line's PIXEL endpoints so it never overlaps either endpoint handle. The
 * anchor sits `handleRadius + gap` px outside the endpoints' bounding box,
 * on whichever side has more room before the canvas edge:
 *   - mostly-horizontal line (|dx| >= |dy|) -> anchor above/below the box
 *     (`top`/`bottom` placement, x = box midpoint).
 *   - mostly-vertical line (|dx| < |dy|) -> anchor left/right of the box
 *     (`left`/`right` placement, y = box midpoint).
 * Pure pixel-space math -- works the same under any projection the caller
 * already applied (plane's raw scale, LiveCanvas's zoom/pan, or a lane's
 * full-height vertical strip), and for degenerate (near-zero-length) lines,
 * since the "box" only needs the clearance to be non-empty.
 */
export const popoverAnchorForLine = (
  p1px,
  p2px,
  canvasSize,
  {
    handleRadius = TRIPWIRE_HANDLE_RADIUS_PX,
    gap = POPOVER_CLEARANCE_GAP_PX,
  } = {},
) => {
  const clearance = handleRadius + gap;
  const minX = Math.min(p1px[0], p2px[0]) - clearance;
  const maxX = Math.max(p1px[0], p2px[0]) + clearance;
  const minY = Math.min(p1px[1], p2px[1]) - clearance;
  const maxY = Math.max(p1px[1], p2px[1]) + clearance;
  const midX = (p1px[0] + p2px[0]) / 2;
  const midY = (p1px[1] + p2px[1]) / 2;
  const dx = Math.abs(p2px[0] - p1px[0]);
  const dy = Math.abs(p2px[1] - p1px[1]);
  const canvasW = canvasSize?.width ?? 0;
  const canvasH = canvasSize?.height ?? 0;

  if (dx >= dy) {
    const roomTop = minY;
    const roomBottom = canvasH - maxY;
    return roomTop >= roomBottom
      ? { x: midX, y: minY, placement: "top" }
      : { x: midX, y: maxY, placement: "bottom" };
  }
  const roomLeft = minX;
  const roomRight = canvasW - maxX;
  return roomLeft >= roomRight
    ? { x: minX, y: midY, placement: "left" }
    : { x: maxX, y: midY, placement: "right" };
};

// ---------------------------------------------------------------------------
// Lane axis (line / tunnel layouts)
// ---------------------------------------------------------------------------

/**
 * line/tunnel layouts model positions as 2-D metres too (spec "Scope"), and
 * the lane AXIS -- the coordinate a subject actually walks along -- is `x`:
 * that is what the viewport (utils/viewport.js) scales against
 * `dimensions.width`, and what the backend's `_calculate_linear_position`
 * IDW-interpolates from stations' `canvas_x` (their `canvas_y` only ever
 * selects which of the two tunnel lanes a station belongs to, never
 * contributes to the walked distance). So "a position along the lane" is an
 * x-in-metres value, and "the full lane" at that position is the vertical
 * (y) span at that x -- a tripwire placed this way is a segment
 * `[[x, 0], [x, spanM]]`.
 *
 * A/B side follows automatically from `sideOfLine`: for p1=(x,0), p2=(x,h),
 * a query point q=(qx, qy) gives `s = -h*(qx-x)`, which is positive (side A)
 * exactly when `qx < x`. So **A = the side reached at smaller x, i.e. the
 * side a subject is on BEFORE the gate when walking in the direction of
 * increasing lane coordinate**; B = after the gate. This matches the "A =
 * before the gate" convention called out in the spec's frontend section.
 */
export const buildLaneSegment = (xMeters, dimensions) => {
  const spanM =
    dimensions?.height && dimensions.height > 0
      ? dimensions.height
      : DEFAULT_LANE_SPAN_M;
  return [
    [xMeters, 0],
    [xMeters, spanM],
  ];
};

// ---------------------------------------------------------------------------
// Client-side validation (mirrors indoor_positioning_tripwire_api.py)
// ---------------------------------------------------------------------------

export const MIN_LINE_LENGTH_M = 0.5;
export const MIN_NAME_LENGTH = 1;
export const MAX_NAME_LENGTH = 100;

export const OVERRIDE_RANGES = {
  band_m: { min: 0.1, max: 10 },
  confirm_samples: { min: 1, max: 10 },
  max_gap_s: { min: 3, max: 600 },
  cooldown_seconds: { min: 0, max: 86400 },
};

/** Ray-casting point-in-polygon; `polygon` is an array of `[x, y]` pairs. */
export const pointInPolygon = (x, y, polygon) => {
  if (!Array.isArray(polygon) || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

/**
 * Mirrors the backend's point/length/bounds/boundary checks so the operator
 * gets an immediate error instead of a round-trip 400. This is a UX
 * convenience only -- the backend remains the source of truth and its 400
 * message is always surfaced too (see the settings modal).
 *
 * @returns {string|null} the first validation error message, or null if ok.
 */
export const validateTripwireGeometry = (
  points,
  { floorWidth, floorHeight, layoutType, boundaryArea, blockedAreas } = {},
) => {
  if (!Array.isArray(points) || points.length !== 2) {
    return "points must be exactly 2 finite [x, y] pairs";
  }
  const [p1, p2] = points;
  for (const p of [p1, p2]) {
    if (
      !Array.isArray(p) ||
      p.length !== 2 ||
      !Number.isFinite(p[0]) ||
      !Number.isFinite(p[1])
    ) {
      return "points must be exactly 2 finite [x, y] pairs";
    }
  }

  if (Number.isFinite(floorWidth) && Number.isFinite(floorHeight)) {
    for (const [x, y] of [p1, p2]) {
      if (!(x >= 0 && x <= floorWidth && y >= 0 && y <= floorHeight)) {
        return `points must be within [0,${floorWidth}]x[0,${floorHeight}]`;
      }
    }
  }

  if (segmentLength(p1, p2) < MIN_LINE_LENGTH_M) {
    return `line length must be at least ${MIN_LINE_LENGTH_M} m`;
  }

  if (
    layoutType === "plane" &&
    Array.isArray(boundaryArea) &&
    boundaryArea.length >= 3
  ) {
    if (
      !pointInPolygon(p1[0], p1[1], boundaryArea) ||
      !pointInPolygon(p2[0], p2[1], boundaryArea)
    ) {
      return "both points must be inside boundary_area (on the edge is ok)";
    }
    for (const hole of blockedAreas || []) {
      const mid = midpoint(p1, p2);
      if (
        pointInPolygon(p1[0], p1[1], hole) ||
        pointInPolygon(p2[0], p2[1], hole) ||
        pointInPolygon(mid[0], mid[1], hole)
      ) {
        return "line must not cross into a blocked area";
      }
    }
  }

  return null;
};

/**
 * `name` is OPTIONAL (2026-09-22(b) revision): missing/null/"" is valid and
 * stored as NULL -- the canvas never draws it and the popover falls back to
 * "未命名越線". Only an over-length name (after trimming) is an error.
 */
export const validateTripwireName = (name) => {
  if (name === null || name === undefined) return null;
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (trimmed.length === 0) return null;
  if (trimmed.length > MAX_NAME_LENGTH) {
    return `名稱長度不可超過 ${MAX_NAME_LENGTH} 字`;
  }
  return null;
};

export const validateOverride = (key, value) => {
  if (value === null || value === undefined || value === "") return null;
  const range = OVERRIDE_RANGES[key];
  if (!range) return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num < range.min || num > range.max) {
    return `需介於 ${range.min} ~ ${range.max}`;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Payload builder (form values -> REST body)
// ---------------------------------------------------------------------------

const blankToNull = (v) =>
  v === undefined || v === null || v === "" ? null : v;

/**
 * Builds the snake_case REST body from editor/settings-modal form values.
 * Blank override inputs become `null` (inherit); an empty exemption list
 * becomes `null` (nobody exempt, matches backend semantics).
 *
 * `name` is optional (2026-09-22(b)): trimmed to a non-empty string, or
 * `null` when blank/missing -- never an empty string. `direction` defaults
 * to `both` (was `a_to_b`), matching the new API/DB default. 排程/`schedule`
 * is REMOVED entirely -- the payload never carries that key, so an
 * unexpected `schedule` on an incoming draft is simply dropped, matching how
 * the backend now ignores any unknown key.
 */
export const buildTripwirePayload = (form) => {
  const trimmedName = typeof form.name === "string" ? form.name.trim() : "";

  const allowedPersonnelIds =
    Array.isArray(form.allowed_personnel_ids) &&
    form.allowed_personnel_ids.length > 0
      ? form.allowed_personnel_ids
      : null;
  const allowedEquipmentIds =
    Array.isArray(form.allowed_equipment_ids) &&
    form.allowed_equipment_ids.length > 0
      ? form.allowed_equipment_ids
      : null;

  return {
    name: trimmedName.length > 0 ? trimmedName : null,
    points: form.points,
    direction: form.direction || "both",
    applies_to: form.applies_to || "both",
    band_m: blankToNull(form.band_m),
    confirm_samples: blankToNull(form.confirm_samples),
    max_gap_s: blankToNull(form.max_gap_s),
    cooldown_seconds: blankToNull(form.cooldown_seconds),
    severity: form.severity || "warning",
    allowed_personnel_ids: allowedPersonnelIds,
    allowed_equipment_ids: allowedEquipmentIds,
    enabled: form.enabled !== false,
  };
};

// ---------------------------------------------------------------------------
// Level-0 drag: translate a whole line (both endpoints) by a delta, clamped
// so both endpoints stay inside the floor rect [0,floorWidth]x[0,floorHeight].
// The delta is clamped as a single unit (not per-point) so the line is only
// ever translated, never distorted -- whichever endpoint is closest to an
// edge limits how far the WHOLE segment may move.
// ---------------------------------------------------------------------------

export const clampTranslation = (points, dx, dy, floorWidth, floorHeight) => {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const dxMin = Math.max(...xs.map((x) => -x));
  const dxMax = Math.min(...xs.map((x) => floorWidth - x));
  const dyMin = Math.max(...ys.map((y) => -y));
  const dyMax = Math.min(...ys.map((y) => floorHeight - y));
  return {
    dx: Math.min(Math.max(dx, dxMin), dxMax),
    dy: Math.min(Math.max(dy, dyMin), dyMax),
  };
};

export const translateSegment = (points, dx, dy, floorWidth, floorHeight) => {
  const clamped = clampTranslation(points, dx, dy, floorWidth, floorHeight);
  return points.map(([x, y]) => [
    Math.round((x + clamped.dx) * 100) / 100,
    Math.round((y + clamped.dy) * 100) / 100,
  ]);
};

// ---------------------------------------------------------------------------
// Level-1 popover content helpers
// ---------------------------------------------------------------------------

// Operator-facing direction collapses to 單向/雙向 (2026-09-22(c)) -- legacy
// `b_to_a` reads as 單向 too (isSingleDirection/restrictedSides above handle
// its geometry defensively; the label just needs to not show a retired
// A/B-worded value).
export const DIRECTION_LABELS = {
  a_to_b: "單向",
  b_to_a: "單向",
  both: "雙向",
};
export const APPLIES_TO_LABELS = {
  personnel: "人員",
  equipment: "機具",
  both: "全部",
};

export const directionLabel = (direction) =>
  DIRECTION_LABELS[direction] || "單向";

export const appliesToLabel = (appliesTo) =>
  APPLIES_TO_LABELS[appliesTo] || appliesTo || "";
